import React, { useState, useContext, useEffect, useRef } from "react";
import { MindmapDashboardPreview } from "./MindmapDashboardPreview";
import { IMindmap, deleteMindmap, getMindmap } from "../../api/mindmap";
import { Spinner } from "../../components/spinner/Spinner";
import { AppContext } from "../../appContext";
import "./MindmapDashboard.css";

interface IMindmapDashboardProps {
    items: IMindmap[];
    loading: boolean;
    onSelect: (item: IMindmap) => void;
    onAddMindmap: () => Promise<IMindmap | null>;
    onDelete: (id: string) => void;
}

interface IContextMenu {
    x: number;
    y: number;
    item: IMindmap;
}

export const MindmapDashboard = ({ items, loading, onSelect, onAddMindmap, onDelete }: IMindmapDashboardProps) => {
    const { translate } = useContext(AppContext);
    const [contextMenu, setContextMenu] = useState<IContextMenu | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    const handleAdd = async () => {
        const created = await onAddMindmap();
        if (created) {
            onSelect(created);
        }
    };

    const handleSelect = (item: IMindmap) => () => {
        onSelect(item);
    };

    const handleContextMenu = (e: React.MouseEvent, item: IMindmap) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ x: e.clientX, y: e.clientY, item });
        setShowDeleteConfirm(false);
    };

    const handleExport = async () => {
        if (!contextMenu) return;
        try {
            const mindmapData = await getMindmap(contextMenu.item.id);
            if (mindmapData) {
                const data = {
                    name: mindmapData.name,
                    objects: mindmapData.json?.objects || [],
                    arrows: mindmapData.json?.arrows || []
                };
                const json = JSON.stringify(data, null, 2);
                const blob = new Blob([json], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${mindmapData.name || 'mindmap'}.json`;
                a.click();
                URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Export failed:', error);
        }
        setContextMenu(null);
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        if (!contextMenu) return;
        try {
            await deleteMindmap(contextMenu.item.id);
            onDelete(contextMenu.item.id);
        } catch (error) {
            console.error('Delete failed:', error);
        }
        setContextMenu(null);
        setShowDeleteConfirm(false);
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
                setContextMenu(null);
                setShowDeleteConfirm(false);
            }
        };
        if (contextMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [contextMenu]);

    if (loading) {
        return <div className="MindmapDashboard"><Spinner /></div>;
    }

    return <div className="MindmapDashboard">
        <div className="MindmapDashboard_list">
            <div className="button MindmapDashboard_item" onClick={handleAdd}>
                <div className="MindmapDashboard_item_preview">
                    <div className="MindmapDashboard_icon--add"></div>
                </div>
            </div>
            {items.map((item, index) => {
                return <div 
                    className="MindmapDashboard_item" 
                    key={item.id || index}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                >
                    <div className="button MindmapDashboard_item_preview" onClick={handleSelect(item)}>
                        <div className="MindmapDashboard_item_content">
                            <MindmapDashboardPreview fileData={item}></MindmapDashboardPreview>
                        </div>
                    </div>
                    <div className="MindmapDashboard_item_name">
                        {item.name}
                    </div>
                </div>
            })}
        </div>
        {contextMenu && (
            <div 
                ref={contextMenuRef}
                className="MindmapDashboard_contextMenu" 
                style={{ left: contextMenu.x, top: contextMenu.y }}
            >
                {!showDeleteConfirm ? (
                    <>
                        <div className="MindmapDashboard_contextMenu_item" onClick={handleExport}>
                            {translate('mindmap_context_export') || 'Export'}
                        </div>
                        <div className="MindmapDashboard_contextMenu_item MindmapDashboard_contextMenu_item--danger" onClick={handleDeleteClick}>
                            {translate('mindmap_context_delete') || 'Delete'}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="MindmapDashboard_contextMenu_confirm">
                            {translate('mindmap_delete_confirm') || 'Delete this board?'}
                        </div>
                        <div className="MindmapDashboard_contextMenu_actions">
                            <div className="MindmapDashboard_contextMenu_item MindmapDashboard_contextMenu_item--danger" onClick={handleDeleteConfirm}>
                                {translate('common_yes') || 'Yes'}
                            </div>
                            <div className="MindmapDashboard_contextMenu_item" onClick={handleDeleteCancel}>
                                {translate('common_no') || 'No'}
                            </div>
                        </div>
                    </>
                )}
            </div>
        )}
    </div>
}
